import { v4 as generateUUID } from 'uuid';
import { DashboardsColumn, RecordAllocation, DashboardsSorting } from '@services/dashboards/types';
import { DashboardsSourceType } from './types';

const stringifyAllocation = (value: RecordAllocation) => Object.values(value).sort().join(' ');

const compareRecordAllocations = (a?: RecordAllocation[], b?: RecordAllocation[]) => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return (
    Array.from(new Set([...a.map(stringifyAllocation), ...b.map(stringifyAllocation)])).length ===
    a.length
  );
};

const compareValues = (a: DashboardsColumn, b: DashboardsColumn) =>
  a.dashboardAttribute === b.dashboardAttribute &&
  a.dashboardTableName === b.dashboardTableName &&
  a.attributeAccess === b.attributeAccess &&
  compareRecordAllocations(a.recordAllocation, b.recordAllocation);

const compareSorting = (a: DashboardsSorting, b: DashboardsSorting) =>
  a.columnName === b.columnName &&
  a.dashboardAttribute === b.dashboardAttribute &&
  a.dashboardTableName === b.dashboardTableName;

export const getAllColumns = (el: DashboardsColumn[]): DashboardsColumn[] =>
  el.flatMap((a) => (a.records ? getAllColumns(a.records) : a));

export const hasSameRecordAllocation = (arr: RecordAllocation[], el?: RecordAllocation) => {
  if (!arr.length) {
    if (!el) return true;
    return false;
  }
  if (!el) return false;
  return arr.map(stringifyAllocation).includes(stringifyAllocation(el));
};

export const appendPresetColumns = (
  dropdown: DashboardsColumn[],
  preset?: DashboardsColumn[],
): DashboardsColumn[] =>
  preset?.map((a) => {
    const match = getAllColumns(dropdown).find((b) => compareValues(a, b));
    return { ...match, ...a, uuid: a.uuid || match?.uuid || generateUUID() };
  }) || [];

export const reconcileColumnIds = (
  arr: DashboardsColumn[],
  dropdown: DashboardsColumn[],
): DashboardsColumn[] => {
  const all = getAllColumns(dropdown);
  let changed = false;

  const next = arr.map((a) => {
    if (all.some((b) => b.uuid === a.uuid)) return a;
    const match = all.find((b) => compareValues(a, b));
    if (!match) return a;
    changed = true;
    return { ...a, uuid: match.uuid };
  });

  return changed ? next : arr;
};

export const appendId = (arr: DashboardsColumn[], prev: DashboardsColumn[]): DashboardsColumn[] =>
  arr.map((a) => ({
    ...a,
    records: a.records ? appendId(a.records, prev) : undefined,
    uuid: !a.uuid
      ? getAllColumns(prev).find((b) => compareValues(a, b))?.uuid || generateUUID()
      : a.uuid,
  }));

export const parseSource = (value: DashboardsSourceType): DashboardsSourceType[] => {
  if (value === 'all') return ['scheduled', 'completed'];
  return [value];
};

export const parseScope = (value?: DashboardsSourceType[]): DashboardsSourceType => {
  if (value?.includes('completed') && value?.includes('scheduled')) return 'all';
  if (!value?.length || value.includes('scheduled')) return 'scheduled';
  return 'completed';
};

export const getSorting = (arr: DashboardsSorting[], value: DashboardsSorting) =>
  arr.find((el) => compareSorting(el, value));

export const setSorting = (arr: DashboardsSorting[], v: DashboardsSorting) => {
  const data = getSorting(arr, v);
  if (!data) return [...(arr.length > 1 ? [arr[1]] : arr), v];
  return arr.map((el) => (compareSorting(el, v) ? { ...el, ...v } : el));
};
